import { Form, Formik } from 'formik'
import React from 'react'
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'

import referralLinkService from '../../../../lib/services/referralLinkService'
import {
  EDIT_CALENDAR_CODE_FORM_FIELDS,
  EditCalendarCodeFormValues,
} from '../../../../pages/emailAndCalendarIntegration/components/ShareableLinks/constants'
import FieldLabel from '../../../form/FieldLabel'
import FormGroup from '../../../form/FormGroup'
import TextInput from '../../../form/TextInput'
import ActionButton from '../../ActionButton'
import InfoBox from '../../InfoBox'
import PersistentOverlayTrigger from '../../PersistentOverlayTrigger'
import { useUpdateCalendarLinkCodeMutation } from '../hooks/useUpdateCalendarLinkCodeMutation'
import { ReferralLinkTableRow } from '../types'

interface Props {
  referralLink: ReferralLinkTableRow
  onHide: () => void
}

const EditReferralLinkModal: React.FC<Props> = ({ referralLink, onHide }) => {
  const updateCalendarLinkCodeMutation = useUpdateCalendarLinkCodeMutation()
  const isProcessing = updateCalendarLinkCodeMutation.isPending
  const previousCodes = referralLink.previousCodes.reverse()
  const initialValues: EditCalendarCodeFormValues = {
    code: referralLink.currentCompanyCode,
  }

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Edit calendar link code</Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={initialValues}
        onSubmit={async (values, formikHelpers) => {
          await updateCalendarLinkCodeMutation.mutateAsync({
            existingReferralLinkCode: referralLink.currentCompanyCode,
            request: { code: values.code },
            formikHelpers,
          })

          onHide()
        }}
      >
        {({ values, dirty }) => {
          const isPristine = !dirty
          const shouldDisableSubmit = isProcessing || isPristine

          const oldUrl = referralLinkService.buildReferralLinkUrl(
            referralLink.currentCompanyCode,
          )
          const newUrl = referralLinkService.buildReferralLinkUrl(values.code)
          const hasEnteredNewCode = dirty && values.code.trim()

          return (
            <Form>
              <Modal.Body>
                <FormGroup>
                  <FieldLabel className="d-flex align-items-center gap-2">
                    <div>Current code</div>

                    {previousCodes.length > 0 && (
                      <span className="text-sm text-primary cursor-default">
                        <PersistentOverlayTrigger
                          id={`previous-code-tooltip-${referralLink.id}`}
                          tooltip={
                            <ul className="mb-0">
                              {previousCodes.map((previousCode) => (
                                <li key={previousCode.code}>
                                  {previousCode.code}
                                </li>
                              ))}
                            </ul>
                          }
                        >
                          (Previous codes)
                        </PersistentOverlayTrigger>
                      </span>
                    )}
                  </FieldLabel>

                  <input
                    type="text"
                    value={referralLink.currentCompanyCode}
                    className="form-control"
                    disabled
                    readOnly
                  />
                </FormGroup>

                <FormGroup>
                  <TextInput
                    label="New code"
                    name={EDIT_CALENDAR_CODE_FORM_FIELDS.code}
                    autoFocus
                  />
                </FormGroup>

                <InfoBox
                  variant="blue"
                  className="border border-gray-300 box-shadow-lg"
                >
                  <p className="mb-0">
                    {referralLink.previousCodes[0] ? (
                      <>
                        Please note that links using the old code will still
                        work. For example, if a user visits the link with the
                        old code at{' '}
                        <a href={oldUrl} target="_blank" rel="noreferrer">
                          {oldUrl}
                        </a>
                        , they'll be redirected to the link with the new code at{' '}
                        <span className="fw-semibold">
                          {hasEnteredNewCode
                            ? newUrl
                            : `${referralLinkService.buildReferralLinkUrl('<new-code>')}`}
                        </span>
                        .
                      </>
                    ) : (
                      <>
                        Please note that links using the old code (
                        <a href={oldUrl} target="_blank" rel="noreferrer">
                          {oldUrl}
                        </a>
                        ) will continue to work.
                      </>
                    )}
                  </p>
                </InfoBox>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>
                  Cancel
                </Button>

                <OverlayTrigger
                  overlay={
                    isPristine ? (
                      <Tooltip>Code hasn't been updated</Tooltip>
                    ) : (
                      <div />
                    )
                  }
                >
                  <div>
                    <ActionButton
                      isProcessing={updateCalendarLinkCodeMutation.isPending}
                      isProcessingText="Updating"
                      type="submit"
                      disabled={shouldDisableSubmit}
                    >
                      Update code
                    </ActionButton>
                  </div>
                </OverlayTrigger>
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default EditReferralLinkModal
